'use client';

import { X } from 'lucide-react';
import { useEffect } from 'react';

import { Button } from '@/components/ui/button';
import { theme } from '@/lib/ui/theme';

interface BaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
}

export function BaseModal({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
}: BaseModalProps) {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-2xl',
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        className={`bg-background border-border relative max-h-[90vh] w-full ${sizeClasses[size]} overflow-y-auto rounded-lg border shadow-lg`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-background border-border sticky top-0 z-10 flex items-center justify-between border-b p-6 pb-4">
          <h2
            id="modal-title"
            className="text-xl font-bold"
            style={{ fontFamily: theme.fonts.body }}
          >
            {title}
          </h2>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-8 w-8"
            aria-label="Close modal"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="p-6 pt-4">{children}</div>
      </div>
    </div>
  );
}
